'use client';

import { useState, useEffect } from 'react';
import { type Product } from '@/lib/productService';
import { useCartStore } from '@/lib/cartStore';

export function useProductCardActions(
  product: Product,
  storeId: string,
  isStoreClosed = false
) {
  const [isAdding, setIsAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showVariantDialog, setShowVariantDialog] = useState(false);

  const addItem = useCartStore((state) => state.addItem);
  const setStoreIdStore = useCartStore((state) => state.setStoreId);

  const isFoodProduct = product.catalogType === 'FOOD';
  const hasOptionGroups = isFoodProduct && product.optionGroups && product.optionGroups.length > 0;
  const hasAdditionals = isFoodProduct && product.additionals?.some((additional) => additional.active !== false);
  const needsFoodSelection = isFoodProduct && (hasOptionGroups || hasAdditionals);
  const isUnlimitedStock = product.unlimitedStock === true;

  const hasVariants = !isFoodProduct && Array.isArray(product.variants) && product.variants.length > 0;
  const variantStockAvailable = hasVariants
    ? product.variants!.some((variant) => {
      const stock = typeof variant.stock === 'number' ? variant.stock : 0;
      return stock > 0;
    })
    : false;
  const productStock = typeof product.stock === 'number' ? product.stock : 0;
  const hasStockAvailable = isUnlimitedStock
    ? true
    : hasVariants
      ? variantStockAvailable
      : productStock > 0;
  const isOutOfStock = !hasStockAvailable;
  const isInactive = product.active === false;
  // Vitrine Viva (VT-491): pre-sale products cannot be added to cart anywhere.
  const isPresale = Boolean(product.launchAt && new Date(product.launchAt).getTime() > Date.now());
  const isUnavailable = isInactive || isOutOfStock || isStoreClosed || isPresale;

  useEffect(() => {
    setStoreIdStore(storeId);
  }, [storeId, setStoreIdStore]);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isAdding || isUnavailable) return;

    if (hasVariants || needsFoodSelection) {
      setShowVariantDialog(true);
      return;
    }

    setIsAdding(true);
    const finalPrice = product.promotionPrice || product.price;

    addItem(
      {
        productId: product.id,
        name: product.name,
        price: finalPrice,
        imageUrl: product.imageUrl,
      },
      1
    );

    setJustAdded(true);
    setTimeout(() => {
      setIsAdding(false);
      setJustAdded(false);
      setShowDialog(true);
    }, 800);
  };

  return {
    isAdding,
    justAdded,
    showDialog,
    setShowDialog,
    showVariantDialog,
    setShowVariantDialog,
    isOutOfStock,
    isInactive,
    isPresale,
    isUnavailable,
    handleAddToCart,
  };
}

export function getCatalogEmoji(catalogType?: string) {
  switch (catalogType) {
    case 'GROCERIES':
      return '🛒';
    case 'FASHION':
      return '👕';
    case 'FOOD':
      return '🍽️';
    default:
      return '📦';
  }
}
